import { useEffect, useRef } from "react";
import { OutlineLabelElement } from "./OutlinePartial";

// --- HELPERS ---
const isFixedElement = (el) => {
    for (let node = el; node && node !== document.documentElement; node = node.parentNode) {
        const pos = window.getComputedStyle(node).position;
        if (pos === 'fixed' || pos === 'sticky') return true;
    }
    return false;
};

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

const applyOutlineStyle = (outlineEl, targetEl, isFixed) => {
    if (!outlineEl) return;
    
    // KHÔNG VẼ VIỀN CHO THẺ ĐÃ "CHẾT" (ĐANG CHỜ MỜ ĐI TRONG 150MS HOẶC BỊ XÓA KHỎI DOM)
    if (!targetEl || !document.contains(targetEl) || targetEl.hasAttribute('data-smind-dead')) {
        outlineEl.style.display = 'none';
        return;
    }

    const rect = targetEl.getBoundingClientRect();
    let top = rect.top, left = rect.left;

    if (!isFixed) {
        for (let p = targetEl.parentNode; p && p !== document.documentElement; p = p.parentNode) {
            top += p.scrollTop || 0;
            left += p.scrollLeft || 0;
        }
        top += window.scrollY;
        left += window.scrollX;
    }

    Object.assign(outlineEl.style, {
        position: isFixed ? 'fixed' : 'absolute',
        top: `${top}px`, left: `${left}px`,
        width: `${rect.width}px`, height: `${rect.height}px`,
        display: 'block'
    });

    outlineEl.classList.toggle('__top', rect.top <= 0);
    outlineEl.classList.toggle('__bottom-inside', (rect.bottom + 50) > window.innerHeight);
};

// Tìm tất cả element "sinh đôi" cùng smind-key trong cùng section-wrapper
// (ví dụ slider clone các element bên trong). Trả về [{ el, isFixed }] KHÔNG gồm primary.
const buildElementMirrors = (primary) => {
    if (!primary) return [];
    const smindKey = primary.getAttribute('smind-key');
    const sectionWrapper = primary.closest('section-wrapper');
    if (!smindKey || !sectionWrapper) return [];

    const all = sectionWrapper.querySelectorAll(`[smind-key="${smindKey}"]:not([data-smind-dead="true"])`);
    const mirrors = [];
    all.forEach((el) => {
        if (el !== primary) mirrors.push({ el, isFixed: isFixedElement(el) });
    });
    return mirrors;
};

// Vị trí của element trong list cùng smind-key — dùng để self-heal sau re-render,
// tránh nhảy sang bản clone sai.
const getElementIndex = (el) => {
    if (!el) return -1;
    const smindKey = el.getAttribute('smind-key');
    const wrapper = el.closest('section-wrapper');
    if (!smindKey || !wrapper) return -1;
    const all = Array.from(wrapper.querySelectorAll(`[smind-key="${smindKey}"]:not([data-smind-dead="true"])`));
    return all.indexOf(el);
};

// Ghi lại "đường đi" (chuỗi chỉ số con) từ section-wrapper xuống node. Path ổn định giữa
// các lần re-render cùng schema — phân biệt chính xác giữa các element cùng smind-key.
const getDOMPath = (el, root) => {
    const path = [];
    let cur = el;
    while (cur && cur !== root && cur.parentNode) {
        const parent = cur.parentNode;
        const idx = Array.prototype.indexOf.call(parent.children, cur);
        if (idx < 0) return null;
        path.unshift(idx);
        cur = parent;
        if (!cur) return null;
    }
    return cur === root ? path : null;
};

const findByDOMPath = (root, path) => {
    if (!root || !path || !path.length) return null;
    let cur = root;
    for (const idx of path) {
        if (!cur || !cur.children || !cur.children[idx]) return null;
        cur = cur.children[idx];
    }
    return cur;
};

// Self-heal: ưu tiên DOM path → index → node đầu tiên. Verify bằng smindKey.
const findElementSmart = (sectionId, smindKey, domPath, index) => {
    if (!sectionId || !smindKey) return null;
    const wrapper = document.querySelector(`section-wrapper[section-id="${sectionId}"]`);
    if (!wrapper) return null;

    const byPath = findByDOMPath(wrapper, domPath);
    if (byPath && byPath.getAttribute?.('smind-key') === smindKey && !byPath.hasAttribute?.('data-smind-dead')) {
        return byPath;
    }

    const all = wrapper.querySelectorAll(`[smind-key="${smindKey}"]:not([data-smind-dead="true"])`);
    if (!all.length) return null;
    if (index >= 0 && all[index]) return all[index];
    return all[0] || null;
};

// --- MAIN COMPONENT ---
function OutlineElement({ activeId, onClick, onTextChange }) {
    const state = useRef({
        activeEl: null, activeIsFixed: false,
        activeSectionId: null, activeSmindKey: null, activePrimaryIndex: -1, activeDOMPath: null,
        hoverEl: null, hoverIsFixed: false,
        hoverSectionId: null, hoverSmindKey: null, hoverPrimaryIndex: -1, hoverDOMPath: null,
        hoverTimer: null, pendingHoverEl: null,
        activeMirrors: [], hoverMirrors: []
    }).current;

    const activeIdRef = useRef(activeId);

    const refs = {
        hoverOut: useRef(null), activeOut: useRef(null),
        hoverTxt: useRef(null), activeTxt: useRef(null),
        hoverSecTxt: useRef(null), activeSecTxt: useRef(null),
        mirrorHost: useRef(null)
    };

    const reconcileMirrorDivs = () => {
        const host = refs.mirrorHost.current;
        if (!host) return;
        const total = state.activeMirrors.length + state.hoverMirrors.length;
        while (host.children.length < total) {
            const div = document.createElement('div');
            div.className = 'smind-outline-element __mirror';
            host.appendChild(div);
        }
        while (host.children.length > total) {
            host.removeChild(host.lastChild);
        }
        for (let i = 0; i < state.activeMirrors.length; i++) {
            const div = host.children[i];
            div.classList.add('--active');
            div.classList.remove('--hover');
        }
        for (let i = 0; i < state.hoverMirrors.length; i++) {
            const div = host.children[state.activeMirrors.length + i];
            div.classList.add('--hover');
            div.classList.remove('--active');
        }
    };

    useEffect(() => {
        activeIdRef.current = activeId;
    }, [activeId]);

    // ==========================================
    // VÒNG LẶP 60 FPS & TỰ ĐỘNG PHỤC HỒI TỪ XÁC THẺ CŨ (SELF-HEALING)
    // ==========================================
    useEffect(() => {
        let frameId;
        const update = () => {
            const currentActive = activeIdRef.current;

            if (currentActive && currentActive.element) {
                const isDead = !state.activeEl || !document.contains(state.activeEl) || state.activeEl.hasAttribute('data-smind-dead');

                if (isDead) {
                    // Ưu tiên self-heal theo DOM path (chính xác cho tabs, sliders...),
                    // fallback theo (sectionId, smindKey, primaryIndex).
                    let targetElement = findElementSmart(state.activeSectionId, state.activeSmindKey, state.activeDOMPath, state.activePrimaryIndex);

                    if (!targetElement) {
                        // Fallback cuối: tìm theo metadata gốc từ sidebar
                        const targetSection = document.querySelector(`section-wrapper[section-id="${currentActive.sectionId}"]`);
                        if (targetSection) {
                            const oldEl = currentActive.element;
                            const smindBlockId = oldEl.getAttribute('smind-block-id');
                            const smindKey = oldEl.getAttribute('smind-key');

                            if (smindBlockId) {
                                targetElement = targetSection.querySelector(`[smind-block-id="${smindBlockId}"]:not([data-smind-dead="true"])`);
                            } else if (smindKey) {
                                targetElement = targetSection.querySelector(`[smind-key="${smindKey}"]:not([data-smind-dead="true"])`);
                            }
                        }
                    }

                    if (targetElement) {
                        const newWrapper = targetElement.closest('section-wrapper');
                        state.activeEl = targetElement;
                        state.activeIsFixed = isFixedElement(targetElement);
                        state.activeSectionId = newWrapper?.getAttribute('section-id') || null;
                        state.activeSmindKey = targetElement.getAttribute('smind-key') || null;
                        state.activePrimaryIndex = getElementIndex(targetElement);
                        state.activeDOMPath = getDOMPath(targetElement, newWrapper);
                        state.activeMirrors = buildElementMirrors(targetElement);
                        updateLabels(targetElement, false);
                        if (refs.activeOut.current) refs.activeOut.current.classList.add('__active', '__show-actions');
                    }
                }
            }

            // Dọn mirror chết/đã bị đánh dấu data-smind-dead
            state.activeMirrors = state.activeMirrors.filter(m => document.contains(m.el) && !m.el.hasAttribute('data-smind-dead'));
            state.hoverMirrors = state.hoverMirrors.filter(m => document.contains(m.el) && !m.el.hasAttribute('data-smind-dead'));

            applyOutlineStyle(refs.activeOut.current, state.activeEl, state.activeIsFixed);
            applyOutlineStyle(refs.hoverOut.current, state.hoverEl, state.hoverIsFixed);

            reconcileMirrorDivs();
            const host = refs.mirrorHost.current;
            if (host) {
                for (let i = 0; i < state.activeMirrors.length; i++) {
                    const m = state.activeMirrors[i];
                    applyOutlineStyle(host.children[i], m.el, m.isFixed);
                }
                for (let i = 0; i < state.hoverMirrors.length; i++) {
                    const m = state.hoverMirrors[i];
                    applyOutlineStyle(host.children[state.activeMirrors.length + i], m.el, m.isFixed);
                }
            }
        };

        const loop = () => { update(); frameId = requestAnimationFrame(loop); };
        loop();

        window.addEventListener('scroll', update, true);
        return () => { cancelAnimationFrame(frameId); window.removeEventListener('scroll', update, true); };
    }, []);

    const resetHover = () => {
        clearTimeout(state.hoverTimer);
        state.hoverEl = null;
        state.hoverIsFixed = false;
        state.hoverSectionId = null;
        state.hoverSmindKey = null;
        state.hoverPrimaryIndex = -1;
        state.hoverDOMPath = null;
        state.hoverMirrors = [];
        if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
    };

    const updateLabels = (elementNode, isHover) => {
        const attr = Array.from(elementNode.attributes).find(a => a.name.startsWith('smind-attr-'));
        let labelName = attr ? attr.name.replace('smind-attr-', '') : 'Element';
        labelName = labelName.replace(/-/g, ' ');

        const parentBlock = elementNode.parentElement?.closest('[smind-block-id]');
        let subLabelName = 'Section';
        let targetType = 'section';

        if (parentBlock) {
            const blockName = parentBlock.getAttribute('smind-block-name');
            const blockId = parentBlock.getAttribute('smind-block-id');
            subLabelName = blockName || capitalize((blockId || '').split('_')[0]);
            targetType = 'block';
        } else {
            const secName = elementNode.closest('section-wrapper')?.getAttribute('data-name');
            subLabelName = secName || 'Section';
            targetType = 'section';
        }

        const targetTxt = isHover ? refs.hoverTxt : refs.activeTxt;
        const targetSecTxt = isHover ? refs.hoverSecTxt : refs.activeSecTxt;

        if (targetTxt.current) targetTxt.current.innerText = capitalize(labelName);
        if (targetSecTxt.current) {
            targetSecTxt.current.innerText = subLabelName;
            const subLabelWrapper = targetSecTxt.current.closest('.smind-sub-label');
            if (subLabelWrapper) subLabelWrapper.setAttribute('data-target-type', targetType);
        }
    };

    const activateElement = (elementNode, isSilent = false) => {
        if (!elementNode) {
            state.activeEl = null;
            state.activeSectionId = null;
            state.activeSmindKey = null;
            state.activePrimaryIndex = -1;
            state.activeDOMPath = null;
            state.activeMirrors = [];
            if (refs.activeOut.current) {
                refs.activeOut.current.style.display = 'none';
                refs.activeOut.current.classList.remove('__active', '__show-actions');
            }
            return;
        }

        const wrapper = elementNode.closest('section-wrapper');
        state.activeEl = elementNode;
        state.activeIsFixed = isFixedElement(elementNode);
        state.activeSectionId = wrapper?.getAttribute('section-id') || null;
        state.activeSmindKey = elementNode.getAttribute('smind-key') || null;
        state.activePrimaryIndex = getElementIndex(elementNode);
        state.activeDOMPath = getDOMPath(elementNode, wrapper);
        state.activeMirrors = buildElementMirrors(elementNode);
        updateLabels(elementNode, false);

        if (onClick && !isSilent) {
            const parentSection = elementNode.closest('section-wrapper');
            if (parentSection) {
                const sectionId = parentSection.getAttribute('section-id');
                const elBlock = elementNode.closest('[smind-block-id]');
                const blockId = elBlock ? elBlock.getAttribute('smind-block-id') : null;
                const smindKey = elementNode.getAttribute('smind-key');
                
                onClick(sectionId, blockId, smindKey);
            }
        }

        resetHover();
        if (refs.activeOut.current) {
            refs.activeOut.current.style.display = 'block';
            refs.activeOut.current.classList.add('__active', '__show-actions');
        }

        window.dispatchEvent(new CustomEvent('smindDeactivateBlock'));
        window.dispatchEvent(new CustomEvent('smindDeactivateSection'));
    };

    // ==========================================
    // XỬ LÝ ACTIVE DOM ELEMENT TỪ PROPS
    // ==========================================
    useEffect(() => {
        // Kiểm tra xem App.jsx có truyền dữ liệu của Element xuống không (có thể là DOM node hoặc String ID)
        const hasElementData = activeId && (activeId.element || activeId.elementId);

        if (hasElementData) {
            const targetSection = document.querySelector(`section-wrapper[section-id="${activeId.sectionId}"]`);
            let targetElement = null;

            if (targetSection) {
                // Rút trích Key để tìm kiếm (Hỗ trợ cả việc Sidebar truyền DOM node hoặc truyền ID)
                const targetKey = activeId.element ? (activeId.element.getAttribute('smind-key') || activeId.element.getAttribute('smind-block-id')) : activeId.elementId;

                if (targetKey) {
                    // Tìm thẻ (bỏ qua các thẻ đang chờ bị xóa)
                    targetElement = targetSection.querySelector(`[smind-block-id="${targetKey}"]:not([data-smind-dead="true"])`)
                                 || targetSection.querySelector(`[smind-key="${targetKey}"]:not([data-smind-dead="true"])`);
                }
            }

            if (targetElement && targetElement !== state.activeEl) {
                activateElement(targetElement, true); // Gọi âm thầm
            } else if (!targetElement) {
                activateElement(null, true);
            }
        } else {
            // NẾU SIDEBAR KHÔNG TRUYỀN ELEMENT (Ví dụ: User chỉ click vào Block) 
            // -> OutlineElement TỰ ĐỘNG BIẾT THÂN BIẾT PHẬN MÀ TẮT ĐI!
            activateElement(null, true);
        }
    }, [activeId]);

    // ==========================================
    // LẮNG NGHE CHUỘT VÀ BÀN PHÍM
    // ==========================================
    useEffect(() => {
        const handleMouse = (e) => {
            const isClick = e.type === 'click';

            const isOwnHoverUI = refs.hoverOut.current?.contains(e.target);
            const isOwnActiveUI = refs.activeOut.current?.contains(e.target);
            const isOwnUI = isOwnHoverUI || isOwnActiveUI;

            if (isOwnUI) {
                if (isClick) {
                    const targetEl = state.hoverEl || state.activeEl;
                    const subLabelClick = e.target.closest('.smind-sub-label');

                    if (subLabelClick && targetEl) {
                        const targetType = subLabelClick.getAttribute('data-target-type');

                        if (targetType === 'block') {
                            const parentBlock = targetEl.parentElement?.closest('[smind-block-id]');
                            if (parentBlock) {
                                const blockId = parentBlock.getAttribute('smind-block-id');
                                const sectionId = targetEl.closest('section-wrapper')?.getAttribute('section-id');                                
                                activateElement(null, true);
                                window.dispatchEvent(new CustomEvent('smindActivateBlock', { detail: { sectionId, blockId } }));
                            }
                        } else {
                            const parentSection = targetEl.closest('section-wrapper');
                            if (parentSection) {
                                const sectionId = parentSection.getAttribute('section-id');
                                activateElement(null, true);
                                window.dispatchEvent(new CustomEvent('smindActivateSection', { detail: sectionId }));
                            }
                        }
                    } else if (e.target.closest('.smind-label')) {
                        if (targetEl) {
                            activateElement(targetEl); 
                        }
                    }
                }

                if (!isClick) {
                    clearTimeout(state.hoverTimer);
                    state.pendingHoverEl = 'OWN_UI';
                }
                return;
            }

            const elementNode = e.target.closest('[smind-attributes]:not([smind-block-id]):not([data-smind-dead="true"])');

            if (refs.activeOut.current && !isClick) {
                refs.activeOut.current.classList.toggle('__show-actions', !!elementNode && elementNode === state.activeEl);
            }

            if (!elementNode) {
                if (isClick) activateElement(null);
                else {
                    if (state.pendingHoverEl !== 'OUTSIDE') {
                        clearTimeout(state.hoverTimer);
                        state.pendingHoverEl = 'OUTSIDE';
                        state.hoverTimer = setTimeout(() => {
                            resetHover();
                        }, 150);
                    }
                }
                return;
            }

            if (isClick) {
                activateElement(elementNode); 
                return;
            }

            if (elementNode === state.hoverEl) {
                clearTimeout(state.hoverTimer);
                state.pendingHoverEl = elementNode;
                return;
            }

            if (state.pendingHoverEl !== elementNode) {
                clearTimeout(state.hoverTimer);
                state.pendingHoverEl = elementNode;
                state.hoverTimer = setTimeout(() => {
                    resetHover();
                    const hoverWrapper = elementNode.closest('section-wrapper');
                    state.hoverEl = elementNode;
                    state.hoverIsFixed = isFixedElement(elementNode);
                    state.hoverSectionId = hoverWrapper?.getAttribute('section-id') || null;
                    state.hoverSmindKey = elementNode.getAttribute('smind-key') || null;
                    state.hoverPrimaryIndex = getElementIndex(elementNode);
                    state.hoverDOMPath = getDOMPath(elementNode, hoverWrapper);
                    state.hoverMirrors = buildElementMirrors(elementNode);
                    updateLabels(elementNode, true);
                }, 100);
            }
        };

        const handleMouseLeaveDocument = () => {
            clearTimeout(state.hoverTimer);
            state.pendingHoverEl = 'OUTSIDE';
            resetHover();
        };

        const handleInput = (e) => {
            const target = e.target;
            
            if (target.matches('[smind-attr-text]') && target.isContentEditable) {
                if (onTextChange) {
                    const parentSection = target.closest('section-wrapper');
                    const sectionId = parentSection ? parentSection.getAttribute('section-id') : null;
                    
                    const parentBlock = target.closest('[smind-block-id]');
                    const blockId = parentBlock ? parentBlock.getAttribute('smind-block-id') : null;
                    
                    const smindKey = target.getAttribute('smind-key');
                    const textContent = target.innerHTML; 

                    onTextChange({
                        sectionId,
                        blockId,
                        smindKey,
                        content: textContent
                    });
                }
            }
        };

        const handleForceDeactivate = () => {
            activeIdRef.current = null;
            activateElement(null, true);
        };

        document.addEventListener('mouseover', handleMouse);
        document.addEventListener('mouseleave', handleMouseLeaveDocument);
        document.addEventListener('click', handleMouse);
        document.addEventListener('input', handleInput); 
        window.addEventListener('smindDeactivateElement', handleForceDeactivate);

        return () => {
            document.removeEventListener('mouseover', handleMouse);
            document.removeEventListener('mouseleave', handleMouseLeaveDocument);
            document.removeEventListener('click', handleMouse);
            document.removeEventListener('input', handleInput);
            window.removeEventListener('smindDeactivateElement', handleForceDeactivate);
            clearTimeout(state.hoverTimer);
        };
    }, [onClick, onTextChange]); 

    return (
        <>
            <div className="smind-outline-element --hover" ref={refs.hoverOut}>
                <OutlineLabelElement textRef={refs.hoverTxt} textSectionRef={refs.hoverSecTxt} />
            </div>

            <div className="smind-outline-element --active" ref={refs.activeOut}>
                <OutlineLabelElement textRef={refs.activeTxt} textSectionRef={refs.activeSecTxt} />
            </div>

            {/* Mirror outlines cho các element trùng smind-key trong cùng section (slider clones...) */}
            <div ref={refs.mirrorHost} />
        </>
    );
}

export default OutlineElement;